import React from 'react';
import { Search, ChevronsUpDown, Check, UserPlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Combobox reutilizable de contactos (cliente / proveedor / etc.) con busqueda
// en servidor y alta inline de nuevos contactos.
export const ContactoCombobox = ({ tipo = 'cliente', value, nombreValue, onChange, testId }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [loadedInitial, setLoadedInitial] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [savingNew, setSavingNew] = React.useState(false);
  const [newForm, setNewForm] = React.useState({ nombre: '', nif_cif: '', email: '', telefono: '' });
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const label = tipo === 'proveedor' ? 'proveedor' : tipo === 'cliente' ? 'cliente' : 'contacto';

  const displayContacto = (c) =>
    c.nombre_completo || [c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(' ') || c.nombre || '';

  const fetchList = React.useCallback(async (query) => {
    setSearching(true);
    try {
      const res = await contactosAPI.getAll({ search: query || undefined, tipo, limit: 50 });
      const data = res.data?.data || res.data || [];
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error buscando ' + label + ':', e);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [tipo, label]);

  React.useEffect(() => {
    if (open && !loadedInitial) {
      fetchList('');
      setLoadedInitial(true);
    }
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    }
  }, [open, loadedInitial, fetchList]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchList(query), 300);
  };

  const handleCreate = async () => {
    if (!newForm.nombre.trim()) {
      toast({ title: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    setSavingNew(true);
    try {
      const res = await contactosAPI.create({
        nombre: newForm.nombre.trim(),
        tipo,
        nif_cif: newForm.nif_cif || null,
        email: newForm.email || null,
        telefono: newForm.telefono || null,
      });
      const nuevo = res.data || {};
      const nombreFinal = nuevo.nombre || newForm.nombre.trim();
      toast({ title: label.charAt(0).toUpperCase() + label.slice(1) + ' creado' });
      onChange(String(nuevo.id), nombreFinal);
      setCreating(false);
      setNewForm({ nombre: '', nif_cif: '', email: '', telefono: '' });
      setOpen(false);
      setSearchQuery('');
      setLoadedInitial(false);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo crear el ' + label, variant: 'destructive' });
    } finally {
      setSavingNew(false);
    }
  };

  const displayName = value ? (nombreValue || ('#' + value)) : ('Buscar ' + label + '...');

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreating(false); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={'truncate ' + (!value ? 'text-gray-400' : '')}>{displayName}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {!creating ? (
          <>
            <div className="flex items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={inputRef}
                type="text"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                placeholder={'Buscar ' + label + ' por nombre o NIF...'}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                data-testid={testId ? testId + '-search' : undefined}
              />
              {searchQuery && (
                <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs"
                  onClick={() => { setSearchQuery(''); fetchList(''); }}>&times;</button>
              )}
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
              {!searching && results.length === 0 && (
                <div className="p-4 text-sm text-center text-gray-500">No se encontraron {label}es</div>
              )}
              {!searching && results.map((c) => (
                <div key={c.id}
                  className={'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ' + (String(value) === String(c.id) ? 'bg-accent/50' : '')}
                  onClick={() => { onChange(String(c.id), displayContacto(c)); setOpen(false); }}
                  data-testid={testId ? testId + '-option-' + c.id : undefined}>
                  <Check className={'mr-2 h-4 w-4 flex-shrink-0 ' + (String(value) === String(c.id) ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{displayContacto(c)}{c.nif_cif ? ' (' + c.nif_cif + ')' : ''}</span>
                </div>
              ))}
            </div>
            <div className="border-t p-2 flex items-center justify-between gap-2">
              <button type="button" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => { setNewForm({ nombre: searchQuery, nif_cif: '', email: '', telefono: '' }); setCreating(true); }}
                data-testid={testId ? testId + '-crear-nuevo' : undefined}>
                <UserPlus className="w-4 h-4" /> Crear nuevo {label}
              </button>
              {value && (
                <button type="button" className="text-xs text-red-500 hover:text-red-700"
                  onClick={() => { onChange('', ''); setOpen(false); }}>Quitar</button>
              )}
            </div>
          </>
        ) : (
          <div className="p-3 space-y-2" data-testid={testId ? testId + '-form-nuevo' : undefined}>
            <p className="text-sm font-medium">Nuevo {label}</p>
            <Input placeholder="Nombre *" value={newForm.nombre}
              onChange={(e) => setNewForm({ ...newForm, nombre: e.target.value })}
              data-testid={testId ? testId + '-nuevo-nombre' : undefined} />
            <Input placeholder="NIF/CIF" value={newForm.nif_cif}
              onChange={(e) => setNewForm({ ...newForm, nif_cif: e.target.value })}
              data-testid={testId ? testId + '-nuevo-nif' : undefined} />
            <Input placeholder="Email" value={newForm.email}
              onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} />
            <Input placeholder="Telefono" value={newForm.telefono}
              onChange={(e) => setNewForm({ ...newForm, telefono: e.target.value })} />
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setCreating(false)}>Cancelar</Button>
              <Button type="button" size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={savingNew}
                onClick={handleCreate}
                data-testid={testId ? testId + '-guardar-nuevo' : undefined}>
                {savingNew && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Guardar
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ContactoCombobox;
